import { Link, useParams } from "react-router-dom";
import { Reveal, PageHero, PrimaryButton } from "@/components/bits";
import { useArticle, useArticles } from "@/lib/articles";
import { ArticleBody } from "@/components/blog/ArticleBody";
import { ArrowLeft, ArrowUpRight, Clock, Loader2 } from "lucide-react";

/* Minimal markdown renderer: "## " headings, "- " lists, blank-line paragraphs. */
function renderBody(body) {
  const blocks = body.split("\n\n");
  const out = [];
  let list = [];
  const flush = (key) => {
    if (list.length) {
      out.push(
        <ul key={key} className="my-5 space-y-2 pl-1">
          {list.map((li, i) => (
            <li key={i} className="flex gap-3 text-slate-700 leading-relaxed">
              <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-signal shrink-0" /> {li}
            </li>
          ))}
        </ul>
      );
      list = [];
    }
  };
  blocks.forEach((b, idx) => {
    const t = b.trim();
    if (!t) return;
    if (t.startsWith("## ")) {
      flush(`l${idx}`);
      out.push(
        <h2 key={idx} className="mt-10 mb-3 font-display text-xl sm:text-2xl font-bold text-ink">
          {t.slice(3)}
        </h2>
      );
    } else if (t.startsWith("- ")) {
      t.split("\n").forEach((line) => {
        const lt = line.trim();
        if (lt.startsWith("- ")) list.push(lt.slice(2));
      });
    } else {
      flush(`l${idx}`);
      out.push(<p key={idx} className="my-5 text-slate-700 leading-relaxed text-[17px]">{t}</p>);
    }
  });
  flush("final");
  return out;
}

export function InsightsPage() {
  const { articles, loading, error, hasMore, loadMore } = useArticles();
  const [featured, ...rest] = articles;
  return (
    <main>
      <PageHero
        eyebrow="Insights"
        title="Industry topics that support smarter shipping decisions."
        testid="insights-title"
        lead="Practical notes from our dispatch desk on California ports, cold chain, compliance and choosing partners."
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {error && <p role="status" className="mb-6 text-sm text-slate-600" data-testid="insights-load-error">New posts are temporarily unavailable. Please try again shortly.</p>}
          {loading && <p role="status" className="mb-6 text-sm text-slate-600 flex items-center gap-2" data-testid="insights-loading"><Loader2 size={16} className="animate-spin" />Loading latest posts…</p>}
          <Reveal>
            <Link
              to={`/insights/${featured.slug}`}
              data-testid="insights-featured"
              className="group grid grid-cols-1 lg:grid-cols-2 card card-hover"
            >
              <div className="h-64 lg:h-auto overflow-hidden">
                <img src={featured.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <p className="label text-[10px] text-signal">Featured · {featured.category} · {featured.date}</p>
                <h2 className="mt-4 font-display text-2xl lg:text-3xl font-bold text-ink leading-tight group-hover:text-corpblue transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-4 text-slate-600 leading-relaxed">{featured.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-corpblue">
                  Read article <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </Reveal>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="insights-grid">
            {rest.map((p, i) => (
              <Reveal key={p.slug} delay={0.07 * i} className="h-full">
                <Link to={`/insights/${p.slug}`} data-testid={`insight-card-${p.slug}`} className="group h-full flex flex-col card card-hover">
                  <div className="h-48 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <p className="label text-[10px] text-signal">{p.category} · {p.date}</p>
                    <h3 className="mt-3 font-display text-lg font-bold text-ink leading-snug group-hover:text-corpblue transition-colors">{p.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{p.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-corpblue">
                      Read more <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          {hasMore && <button disabled={loading} onClick={loadMore} className="btn-pill primary-action mt-8 px-6 py-3" data-testid="insights-load-more">{loading ? "Loading…" : "Load more posts"}</button>}
        </div>
      </section>
    </main>
  );
}

export function InsightDetail() {
  const { slug } = useParams();
  const { article: p, loading, error } = useArticle(slug);
  const { articles } = useArticles(3);
  if (loading) return <main className="min-h-[50vh] max-w-5xl mx-auto px-6 py-20 flex gap-3 items-center" data-testid="article-loading"><Loader2 className="animate-spin text-corpblue" size={20} />Loading article…</main>;
  if (!p || error) return <main className="max-w-5xl mx-auto px-6 py-20 min-h-[50vh]"><h1 className="font-display text-4xl" data-testid="article-not-found">{error === "unavailable" ? "Articles are unavailable right now." : "This article is not available."}</h1><Link to="/insights" className="mt-8 inline-flex text-corpblue font-semibold" data-testid="article-back-to-insights">Back to Insights</Link></main>;
  const more = articles.filter((x) => x.slug !== slug).slice(0, 2);

  return (
    <main>
      <section className="hero-reference relative overflow-hidden text-white">
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pt-10 lg:pt-14 pb-40 lg:pb-48">
          <Reveal>
            <Link to="/insights" data-testid="insight-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:underline underline-offset-4 transition-colors">
              <ArrowLeft className="h-4 w-4" /> All insights
            </Link>
          </Reveal>
          <Reveal delay={0.06}>
            <p className="mt-8 label text-brand-light">
              {p.category} · {p.date} · <Clock className="inline h-3 w-3" /> {p.readTime}
            </p>
          </Reveal>
          {p.author && <p className="mt-5 text-sm text-slate-300" data-testid="insight-author">By {p.author}</p>}
          <Reveal delay={0.12}>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]" data-testid="insight-detail-title">
              {p.title}
            </h1>
          </Reveal>
        </div>
      </section>

      <article className="relative z-10 -mt-32 lg:-mt-36">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="card">
              <div className="img-frame overflow-hidden"><img src={p.image} alt={p.cover_alt || p.title} className="img-zoom w-full aspect-[16/9] max-h-[480px] object-contain bg-paper" data-testid="insight-detail-image" /></div>
              <div className="max-w-3xl mx-auto px-6 sm:px-10 py-10 lg:py-14" data-testid="insight-detail-body">
                {p.body_html ? <ArticleBody html={p.body_html} testid="published-article-content" /> : renderBody(p.body || "")}
              </div>
            </div>
          </Reveal>
        </div>
      </article>

      <section className="py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="label text-signal mb-6">Keep reading</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {more.map((m) => (
              <Link key={m.slug} to={`/insights/${m.slug}`} data-testid={`insight-more-${m.slug}`} className="group card card-hover p-7">
                <p className="label text-[10px] text-signal">{m.category}</p>
                <h3 className="mt-3 font-display text-lg font-bold text-ink group-hover:text-corpblue transition-colors">{m.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">{m.excerpt}</p>
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center">
            <PrimaryButton to="/quote" testid="insight-quote-cta">Get a freight quote</PrimaryButton>
          </div>
        </div>
      </section>
    </main>
  );
}
